import type { User } from "firebase/auth";
import { type FormEvent, type KeyboardEvent, useEffect, useRef, useState } from "react";
import * as api from "./api";
import {
  chipLabels,
  labelChanges,
  type LabelRow,
  labelRowProblem,
  labelRows,
  labelRowsIssue,
  matchLabels,
  moreLabels,
  moveRow,
  noMatchText,
  noteCountText,
  noteProblem,
  tagLabels,
  TEXT_MAX,
  toggleLabel,
  visitLabelTexts,
  visitNoteText,
} from "./visitLabels";
import { NOTHING_REASONS, REASON_TEXT } from "./visits";
import { SkeletonSection } from "./Skeleton";

const REASON_NAMES = NOTHING_REASONS.map((reason) => REASON_TEXT[reason]);

/** Rule V-19: the chips of the common labels, and a "More labels" box that finds the others. */
export function VisitLabelPicker({
  list,
  chosen,
  onChange,
}: {
  list: api.VisitLabels;
  chosen: readonly string[];
  onChange: (labels: string[]) => void;
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const chips = chipLabels(list);
  const tags = tagLabels(list, chosen);
  const matches = matchLabels(list, chosen, query);
  const at = Math.min(active, Math.max(matches.length - 1, 0));
  const shown = open && matches.length > 0;
  const add = (label: api.VisitLabel | undefined) => {
    if (!label) return;
    console.info(`visit form: label ${label.code} added from More labels`);
    onChange(toggleLabel(chosen, label.code));
    setQuery("");
    setActive(0);
    input.current?.focus();
  };
  const keys = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      if (shown) event.preventDefault();
      setOpen(false);
    } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);
      if (matches.length) setActive((at + (event.key === "ArrowDown" ? 1 : -1) + matches.length) % matches.length);
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (shown) add(matches[at]);
    } else if (event.key === "Backspace" && !query && tags.length) {
      onChange(chosen.filter((code) => code !== tags[tags.length - 1].code));
    }
  };
  return (
    <fieldset className="visit-labels" data-testid="visit-labels">
      <legend>Labels</legend>
      <div className="visit-label-chips">
        {chips.map((label) => (
          <button
            key={label.code}
            type="button"
            className="chip-choice"
            data-testid="visit-label-chip"
            data-code={label.code}
            aria-pressed={chosen.includes(label.code)}
            onClick={() => onChange(toggleLabel(chosen, label.code))}
          >
            {label.text}
          </button>
        ))}
      </div>
      {tags.length > 0 && (
        <ul className="visit-label-tags" aria-label="More labels on this visit" data-testid="visit-label-tags">
          {tags.map((label) => (
            <li key={label.code} className="label-tag" data-code={label.code}>
              {label.text}
              <button
                type="button"
                className="label-tag-remove"
                data-testid="visit-label-remove"
                aria-label={`Remove the label ${label.text}`}
                onClick={() => {
                  onChange(chosen.filter((code) => code !== label.code));
                  input.current?.focus();
                }}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      {moreLabels(list).length > 0 && (
        <div className="visit-label-more">
          <label htmlFor="visit-label-input">More labels</label>
          <input
            ref={input}
            id="visit-label-input"
            type="text"
            role="combobox"
            aria-expanded={shown}
            aria-controls="visit-label-list"
            aria-autocomplete="list"
            aria-activedescendant={shown ? `visit-label-option-${at}` : undefined}
            autoComplete="off"
            spellCheck={false}
            placeholder="Type to find a label"
            data-testid="visit-label-input"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setActive(0);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onBlur={() => setOpen(false)}
            onKeyDown={keys}
          />
          <ul id="visit-label-list" role="listbox" aria-label="Labels" className="visit-label-list" hidden={!shown}>
            {matches.map((label, index) => (
              <li
                key={label.code}
                id={`visit-label-option-${index}`}
                role="option"
                aria-selected={index === at}
                data-testid="visit-label-option"
                data-code={label.code}
                onMouseDown={(event) => event.preventDefault()}
                onMouseMove={() => setActive(index)}
                onClick={() => add(label)}
              >
                {label.text}
              </li>
            ))}
          </ul>
          {open && query.trim() && !matches.length && (
            <p className="sub" role="status" data-testid="visit-label-none">
              {noMatchText(list, query, REASON_NAMES)}
            </p>
          )}
        </div>
      )}
    </fieldset>
  );
}

/** The note of a visit, 500 characters at most, with the count. A longer note is not cut: it shows a message. */
export function VisitNote({ value, onChange, error }: { value: string; onChange: (note: string) => void; error?: string | null }) {
  const over = noteProblem(value);
  return (
    <div className="visit-note">
      <label htmlFor="visit-note">Note</label>
      <textarea
        id="visit-note"
        data-testid="visit-note"
        rows={2}
        aria-invalid={over ? true : undefined}
        aria-describedby={error ? "visit-error visit-note-count" : "visit-note-count"}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
      <span id="visit-note-count" className={over ? "error visit-note-count" : "sub visit-note-count"} data-testid="visit-note-count" data-over={over ? true : undefined}>
        {noteCountText(value)}
        {over && !error && <span data-testid="visit-note-over"> · {over}</span>}
      </span>
      {error && (
        <p id="visit-error" role="alert" className="error" data-testid="visit-error">
          {error}
        </p>
      )}
    </div>
  );
}

/** The labels and the note of a saved visit, under its outcome. */
export function VisitTags({ visit }: { visit: Pick<api.Visit, "kind" | "note" | "source" | "labels" | "label_text"> }) {
  const labels = visitLabelTexts(visit);
  const note = visitNoteText(visit);
  if (!labels.length && !note) return null;
  return (
    <span className="visit-tags" data-testid="visit-tags">
      {labels.map((text) => (
        <span key={text} className="label-tag" data-testid="visit-tag">
          {text}
        </span>
      ))}
      {note && (
        <span className="sub visit-tag-note" data-testid="visit-tag-note">
          {note}
        </span>
      )}
    </span>
  );
}

/** Admin > Visit labels: add, rename, order and turn off the labels (rule V-19). */
export function VisitLabelSettings({ user }: { user: User }) {
  const [found, setFound] = useState<api.VisitLabels | "failed" | undefined>(undefined);
  const [rows, setRows] = useState<LabelRow[]>([]);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  const [bad, setBad] = useState<{ key: string; text: string } | null>(null);
  const status = useRef<HTMLParagraphElement>(null);
  const show = (next: api.VisitLabels) => {
    setFound(next);
    setRows(labelRows(next));
  };
  useEffect(() => {
    user
      .getIdToken()
      .then(api.visitLabels)
      .then(show, () => setFound("failed"));
  }, [user]);
  if (found === undefined) return <SkeletonSection title="Visit labels" rows={13} phoneRows={19} />;
  if (found === "failed") return <p className="error">Cannot load the visit labels. Try again later.</p>;
  const max = found.text_max ?? TEXT_MAX;
  const edit = (key: string, fields: Partial<LabelRow>) => {
    setRows((all) => all.map((row) => (row.key === key ? { ...row, ...fields } : row)));
    setMessage(null);
    if (bad?.key === key) setBad(null);
  };
  const remove = (key: string) => {
    console.info("visit labels: a new row is removed before the save");
    setRows((all) => all.filter((row) => row.key !== key));
    if (bad?.key === key) setBad(null);
    setMessage(null);
    requestAnimationFrame(() => document.querySelector<HTMLButtonElement>('[data-testid="visit-label-add"]')?.focus());
  };
  const move = (index: number, step: -1 | 1) => {
    setRows((all) => moveRow(all, index, step));
    setMessage(null);
  };
  const add = () => {
    const key = crypto.randomUUID();
    setRows((all) => [...all, { key, code: null, text: "", active: true }]);
    requestAnimationFrame(() => document.getElementById(`visit-label-${key}`)?.focus());
  };
  const save = async (event: FormEvent) => {
    event.preventDefault();
    setTried(true);
    const issue = labelRowsIssue(rows, max);
    if (issue) {
      console.info("visit labels: the save is blocked by a row with a problem");
      setBad(issue);
      setMessage(null);
      requestAnimationFrame(() => document.getElementById(`visit-label-${issue.key}`)?.focus());
      return;
    }
    setBad(null);
    setSaving(true);
    setMessage(null);
    try {
      const next = await api.setVisitLabels(await user.getIdToken(), labelChanges(rows));
      show(next);
      const on = next.labels.filter((label) => label.active).length;
      setMessage({ ok: true, text: `Saved. Drivers see ${on} labels, the first ${Math.min(on, next.chips ?? 8)} as chips.` });
      requestAnimationFrame(() => status.current?.focus());
    } catch (e) {
      const detail = e instanceof api.ApiError && e.status < 500 ? e.detail : undefined;
      setMessage({ ok: false, text: detail ?? "The labels were not saved. Try again." });
    } finally {
      setSaving(false);
    }
  };
  const chips = found.chips ?? 8;
  const chipKeys = new Set(rows.filter((row) => row.active).slice(0, chips).map((row) => row.key));
  return (
    <form onSubmit={save} noValidate data-testid="visit-label-settings">
      <fieldset disabled={!found.can_edit || saving}>
        <legend>Visit labels</legend>
        <p className="hint">
          A driver puts labels on a visit with one tap. The first {chips} labels that are on show as chips on Record a visit. The others are in the More labels box. A label
          that is off stays on old visits.
        </p>
        <ol className="visit-label-rows">
          {rows.map((row, index) => {
            const chip = chipKeys.has(row.key);
            const above = rows.slice(0, index).map((other) => other.text.trim().toLowerCase());
            const wrong = tried && labelRowProblem(row, above, max) !== null;
            const shown = bad?.key === row.key ? bad.text : null;
            return (
              <li key={row.key} data-testid="visit-label-row" data-code={row.code ?? ""}>
                <label htmlFor={`visit-label-${row.key}`} className="visually-hidden">
                  Label {index + 1}
                </label>
                <input
                  id={`visit-label-${row.key}`}
                  data-testid="visit-label-text"
                  value={row.text}
                  maxLength={max + 10}
                  aria-invalid={wrong || undefined}
                  aria-describedby={shown ? `visit-label-error-${row.key}` : undefined}
                  onChange={(e) => edit(row.key, { text: e.target.value })}
                />
                <label className="check">
                  <input type="checkbox" data-testid="visit-label-active" checked={row.active} onChange={(e) => edit(row.key, { active: e.target.checked })} />
                  On
                </label>
                {chip && <span className="chip-flag neutral">Chip</span>}
                {found.can_edit && (
                  <>
                    <button type="button" data-testid="visit-label-up" disabled={index === 0} aria-label={`Move ${row.text || `label ${index + 1}`} up`} onClick={() => move(index, -1)}>
                      ↑
                    </button>
                    <button
                      type="button"
                      data-testid="visit-label-down"
                      disabled={index === rows.length - 1}
                      aria-label={`Move ${row.text || `label ${index + 1}`} down`}
                      onClick={() => move(index, 1)}
                    >
                      ↓
                    </button>
                    {row.code === null && (
                      <button type="button" className="quiet" data-testid="visit-label-remove-row" onClick={() => remove(row.key)}>
                        Remove
                      </button>
                    )}
                  </>
                )}
                {shown && (
                  <p id={`visit-label-error-${row.key}`} className="error visit-label-error" role="alert" data-testid="visit-label-error">
                    {shown}
                  </p>
                )}
              </li>
            );
          })}
        </ol>
        {found.can_edit && (
          <div className="actions">
            <button type="button" data-testid="visit-label-add" onClick={add}>
              Add a label
            </button>
            <button className="primary" data-testid="visit-label-save">
              {saving ? "Saving…" : "Save the labels"}
            </button>
          </div>
        )}
      </fieldset>
      <p ref={status} tabIndex={-1} role="status" data-testid="visit-label-status" className={message && !message.ok ? "error" : undefined}>
        {message?.text}
      </p>
    </form>
  );
}
